import { useEffect, useState, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  doc,
  serverTimestamp,
  setDoc,
  onSnapshot
} from "firebase/firestore";
import { db } from "../firebase/firebase";

const INITIAL_BOT_MESSAGE = {
  id: 1,
  from: "bot",
  text: "Hi! How can i help you?",
  ts: new Date().toISOString(),
};

const MAX_MESSAGES_PER_THREAD = 300;

/**
 * @param {{ userId?: string | null, pageSize?: number }} options
 */
export function useThreads(options = {}) {
  const { userId, pageSize = 20 } = options;

  const [threads, setThreads] = useState(() => {
    const stored = localStorage.getItem("jacaiThreads");
    if (stored) return JSON.parse(stored);

    const first = {
      id: crypto.randomUUID(),
      title: "Chat 1",
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem("jacaiThreads", JSON.stringify([first]));
    localStorage.setItem("jacaiActiveThreadId", first.id);
    return [first];
  });

  const [activeThreadId, setActiveThreadId] = useState(() => {
    return (
      localStorage.getItem("jacaiActiveThreadId") ||
      (threads[0] && threads[0].id) ||
      null
    );
  });

  const [threadMessages, setThreadMessages] = useState(() => {
    const stored = localStorage.getItem("jacaiThreadMessages");
    if (stored) return JSON.parse(stored);
    return {};
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [error, setError] = useState(null);

  const mergeThreads = useCallback((current, incoming) => {
    const map = new Map();
    [...incoming, ...current].forEach((t) => {
      map.set(t.id, t);
    });
    return Array.from(map.values()).sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, []);

  const updateThreadInState = useCallback((id, patch) => {
  setThreads((prev) =>
    prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
    );
  }, []);

  useEffect(() => {
    if (!userId || !db) return;

    let cancelled = false;

    async function loadInitial() {
      setIsLoading(true);
      setError(null);
      try {
        const qRef = query(
          collection(db, "conversations", userId, "threads"),
          orderBy("createdAt", "desc"),
          limit(pageSize)
        );

        const snap = await getDocs(qRef);
        if (cancelled) return;

        const loaded = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title || "New chat",
            createdAt: data.createdAt?.toDate
              ? data.createdAt.toDate().toISOString()
              : new Date().toISOString(),
          };
        });

        setThreads((prev) => mergeThreads(prev, loaded));
        setLastVisible(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.size === pageSize);

        if (!activeThreadId && loaded[0]) {
          setActiveThreadId(loaded[0].id);
        }
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load threads");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [userId, pageSize, mergeThreads, activeThreadId]);

  useEffect(() => {
    if (!userId || !db || !activeThreadId) return;

    const ref = doc(db, "conversations", userId, "threads", activeThreadId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;

        const data = snap.data();

        updateThreadInState(activeThreadId, {
          title: data.title || "New chat",
          mode: data.mode,
          cvStep: data.cvStep,
          hasCvData: !!data.cvDraft,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate().toISOString()
            : undefined,
        });
      },
      (err) => console.error("Failed to listen active thread", err)
    );

    return () => unsub();
  }, [userId, activeThreadId, updateThreadInState]);

  useEffect(() => {
    localStorage.setItem("jacaiThreads", JSON.stringify(threads));
  }, [threads]);

  useEffect(() => {
    if (activeThreadId) {
      localStorage.setItem("jacaiActiveThreadId", activeThreadId);
    }
  }, [activeThreadId]);

  useEffect(() => {
    localStorage.setItem(
      "jacaiThreadMessages",
      JSON.stringify(threadMessages)
    );
  }, [threadMessages]);

  useEffect(() => {
    if (!userId || !db || !activeThreadId) return;

    let cancelled = false;

    async function loadMessages() {
      try {
        const colRef = collection(
          db,
          "conversations",
          userId,
          "threads",
          activeThreadId,
          "messages"
        );

        const qRef = query(colRef, orderBy("timestamp", "asc"));

        const snap = await getDocs(qRef);
        if (cancelled) return;

        const loaded = snap.docs.map((d) => {
          const data = d.data();
          let feedback = null;
          if (data.feedback && typeof data.feedback.useful === "boolean") {
            feedback = data.feedback.useful ? "up" : "down";
          }

          return {
            id: d.id,
            from: data.role === "user" ? "user" : "assistant",
            text: data.text,
            ts: data.timestamp?.toDate
              ? data.timestamp.toDate().toISOString()
              : new Date().toISOString(),
            feedback: feedback,
          };
        });

        setThreadMessages((prev) => ({
          ...prev,
          [activeThreadId]:
            loaded.length > 0 ? loaded : [INITIAL_BOT_MESSAGE],
        }));
      } catch (e) {
        console.error("Failed to load messages", e);
      }
    }

    loadMessages();

    return () => {
      cancelled = true;
    };
  }, [userId, activeThreadId]);

  const currentMessages =
    (activeThreadId && threadMessages[activeThreadId]) || [
      INITIAL_BOT_MESSAGE,
    ];

  const loadMoreThreads = useCallback(
    async () => {
      if (!userId || !db) return;
      if (!hasMore || !lastVisible || isLoadingMore) return;

      setIsLoadingMore(true);
      setError(null);

      try {
        const qRef = query(
          collection(db, "conversations", userId, "threads"),
          orderBy("createdAt", "desc"),
          startAfter(lastVisible),
          limit(pageSize)
        );

        const snap = await getDocs(qRef);
        const loaded = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title || "New chat",
            createdAt: data.createdAt?.toDate
              ? data.createdAt.toDate().toISOString()
              : new Date().toISOString(),
          };
        });

        setThreads((prev) => mergeThreads(prev, loaded));
        setLastVisible(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.size === pageSize);
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load more threads");
      } finally {
        setIsLoadingMore(false);
      }
    },
    [userId, hasMore, lastVisible, isLoadingMore, pageSize, mergeThreads]
  );

  const addMessageToActiveThread = (msg) => {
    if (!activeThreadId) return;
    setThreadMessages((prev) => {
      const existing = prev[activeThreadId] || [INITIAL_BOT_MESSAGE];
      const next = [...existing, msg].slice(-MAX_MESSAGES_PER_THREAD);
      return {
        ...prev,
        [activeThreadId]: next,
      };
    });
  };

  const newThread = useCallback(
    async () => {
      if (userId && db) {
        try {
          const title = `Chat ${threads.length + 1}`;
          const ref = await addDoc(
            collection(db, "conversations", userId, "threads"),
            {
              title,
              createdAt: serverTimestamp(),
            }
          );

          const t = {
            id: ref.id,
            title,
            createdAt: new Date().toISOString(),
          };

          setThreads((prev) => [t, ...prev]);
          setActiveThreadId(t.id);
          setThreadMessages((prev) => ({
            ...prev,
            [t.id]: [INITIAL_BOT_MESSAGE],
          }));
          return;
        } catch (e) {
          console.error(
            "Failed to create thread in Firestore, fallback local",
            e
          );
        }
      }

      const t = {
        id: crypto.randomUUID(),
        title: `Chat ${threads.length + 1}`,
        createdAt: new Date().toISOString(),
      };
      setThreads((prev) => [t, ...prev]);
      setActiveThreadId(t.id);
      setThreadMessages((prev) => ({
        ...prev,
        [t.id]: [INITIAL_BOT_MESSAGE],
      }));
    },
    [userId, threads.length]
  );

  const selectThread = (id) => {
    setActiveThreadId(id);
  };

  const renameActiveThreadIfDefault = (text) => {
    if (!activeThreadId) return;

    const newTitle = text.slice(0, 40);
    if (!newTitle) return;

    setThreads((prev) =>
      prev.map((t) =>
        t.id === activeThreadId && t.title.startsWith("Chat ")
          ? { ...t, title: newTitle }
          : t
      )
    );

    if (userId && db) {
      const ref = doc(db, "conversations", userId, "threads", activeThreadId);
      setDoc(
        ref,
        {
          title: newTitle,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((e) =>
        console.error("Failed to rename thread in Firestore", e)
      );
    }
  };

  const updateMessageInActiveThread = (msgId, patch) => {
    if (!activeThreadId) return;

    setThreadMessages((prev) => {
      const existing = prev[activeThreadId] || [];

      const updated = existing.map((m) =>
        m.id === msgId ? { ...m, ...patch } : m
      );

      return {
        ...prev,
        [activeThreadId]: updated,
      };
    });
  };

  return {
    threads,
    activeThreadId,
    currentMessages,
    addMessageToActiveThread,
    newThread,
    selectThread,
    renameActiveThreadIfDefault,
    updateMessageInActiveThread,
    loadMoreThreads,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
  };
}